/**
 * GridScrollArrows.tsx — floating ◀/▶ horizontal-scroll arrows for wide grids
 * (BUG-2026-0159). Same visual language as the schedule rail's
 * Phase2RailScrollArrows: paper-alpha circular buttons that hover over the
 * grid, vertically centred in the visible scrollport via a zero-height sticky
 * overlay. Rendered INSIDE the scroll container, before the table. Each arrow
 * only shows when there is room to scroll that direction.
 */

import React from 'react';
import { Box, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { alpha, useTheme } from '@mui/material/styles';

export interface GridScrollArrowsProps {
  canScrollLeft: boolean;
  canScrollRight: boolean;
  /** Page the container one viewport left (-1) or right (+1). */
  onScroll: (dir: -1 | 1) => void;
  /**
   * Pixels to inset the LEFT arrow so it floats just right of a frozen-column
   * pane instead of on top of it. 0 when nothing is frozen.
   */
  leftInset?: number;
  /**
   * Machine Schedule page-flow mode: an external shim positions the overlay via
   * transform, so the built-in `sticky top:50%` must be OFF — sticky offsets
   * apply even without visible vertical scrolling (the scroll container's
   * h-scrollbar is enough), and the two stacked, pushing the arrows to the
   * grid's bottom edge. Default false = self-centering sticky (EditableGrid).
   */
  disableSticky?: boolean;
}

// forwardRef (additive): the Machine Schedule grid's page-flow sticky shim
// translates this overlay to keep the arrows in view while the page scrolls.
export const GridScrollArrows = React.forwardRef<HTMLDivElement, GridScrollArrowsProps>(({
  canScrollLeft,
  canScrollRight,
  onScroll,
  leftInset = 0,
  disableSticky = false,
}, ref) => {
  const theme = useTheme();
  const btnSx = {
    position: 'absolute' as const,
    top: 0,
    transform: 'translateY(-50%)',
    pointerEvents: 'auto' as const,
    backgroundColor: alpha(theme.palette.background.paper, 0.92),
    color: theme.palette.primary.main,
    border: `1px solid ${theme.border.color}`,
    boxShadow: 2,
    '&:hover': { backgroundColor: theme.palette.background.paper },
  };
  return (
    <Box
      ref={ref}
      aria-hidden="true"
      sx={{
        position: 'sticky',
        top: disableSticky ? 0 : '50%',
        left: 0,
        height: 0,
        zIndex: 6,
        pointerEvents: 'none',
      }}
    >
      {canScrollLeft && (
        <IconButton
          onClick={() => onScroll(-1)}
          aria-label="Scroll columns left"
          sx={{ ...btnSx, left: leftInset + 8 }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}
      {canScrollRight && (
        <IconButton
          onClick={() => onScroll(1)}
          aria-label="Scroll columns right"
          sx={{ ...btnSx, right: 8 }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}
    </Box>
  );
});
GridScrollArrows.displayName = 'GridScrollArrows';
